import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// Brand colour mapped from the previous MUI theme. `theme/palette.js`
// wired `primary.main` to `#2F80ED` (Betterfield blue); the rest are
// the standard semantic tokens. Charts read these as plain strings.
const BF_PALETTE = {
  primary: "#2F80ED",
  success: "#10B981",
  info: "#3B82F6",
  error: "#EF4444",
};

import { DataTable } from "@/components/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer, PageHeader } from "@/components/layout";
import LineChart from "../../components/charts/line-chart";
import PieChart from "../../components/charts/pie-chart";
import InfoPopover from "../../components/info-popover";
import DashboardInfoCard from "../../components/dashboard-info-card";
import InsightsSummaryCard from "../../components/insights/insights-summary-card";
import InspectionsCalendar from "../../components/inspections-calendar";
import ControlDetailsPreview from "../../components/inspections-calendar/ControlDetailsPreview";
import ScheduleInspectionDialog from "../../components/schedule-inspection-dialog";
import { Permissions } from "../../entities/permissions";
import { useQueryState } from "../../utils/query-state";
import { hasPermission } from "../login/selectors";
import {
  useCancelCalendarEventMutation,
  useDashboardCardsQuery,
  useDashboardChartsQuery,
  useLazyCalendarControllersQuery,
  useLazyCalendarEventsQuery,
  useRecentControlsQuery,
  useScheduleInspectionMutation,
  useUpdateControllerColorMutation,
  useUpdateEventDateMutation,
} from "../../services/api";

import generateColumns, { type ControlRow } from "./columns";

interface CardItem {
  label: string;
  count: number | string;
  suffix?: string;
}

interface CardsSlice {
  data: CardItem[];
  loading: boolean;
}

interface ChartsSlice {
  controlsOverTime: unknown[];
  complianceDistribution: unknown[];
  loading: boolean;
}

interface RecentControlsSlice {
  data: ControlRow[];
  loading: boolean;
}

interface CalendarEvent {
  controlId?: number | string;
  type?: string;
  [key: string]: unknown;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cardsQuery = useDashboardCardsQuery();
  const chartsQuery = useDashboardChartsQuery();
  const recentControlsQuery = useRecentControlsQuery();

  const cards: CardsSlice = {
    data: (cardsQuery.data as CardItem[]) || [],
    loading: cardsQuery.isLoading,
  };
  const charts: ChartsSlice = {
    controlsOverTime:
      (chartsQuery.data as { controlsOverTime?: unknown[] })?.controlsOverTime ?? [],
    complianceDistribution:
      (chartsQuery.data as { complianceDistribution?: unknown[] })?.complianceDistribution ?? [],
    loading: chartsQuery.isLoading,
  };
  const recentControls: RecentControlsSlice = {
    data: (recentControlsQuery.data as ControlRow[]) || [],
    loading: recentControlsQuery.isLoading,
  };

  const [triggerCalendarEvents] = useLazyCalendarEventsQuery();
  const [triggerCalendarControllers] = useLazyCalendarControllersQuery();
  const [updateControllerColor] = useUpdateControllerColorMutation();
  const [cancelCalendarEvent] = useCancelCalendarEventMutation();
  const [updateEventDate] = useUpdateEventDateMutation();
  const [scheduleInspection] = useScheduleInspectionMutation();

  const canSeeAllEvents = useSelector(
    hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_SEE_ALL_EVENTS)
  );
  const canRescheduleEvents = useSelector(
    hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_RESCHEDULE_EVENTS)
  );
  const canCancelOverdueEvents = useSelector(
    hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_CANCEL_OVERDUE_EVENTS)
  );
  const canCreateControl = useSelector(
    hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_CREATE_CONTROL)
  );

  // Calendar state persisted in URL.
  const [calendarView, setCalendarView] = useQueryState({
    paramName: "calendarView",
    fromQuery: (value: string | null) => value || "dayGridMonth",
    toQuery: (value: string) => value,
    defaultValue: "dayGridMonth",
  });

  const [calendarDate, setCalendarDate] = useQueryState({
    paramName: "calendarDate",
    fromQuery: (value: string | null) => (value ? new Date(value) : null),
    toQuery: (value: Date | null) =>
      value instanceof Date ? value.toISOString().split("T")[0] : (value ?? ""),
    defaultValue: null as Date | null,
  });

  const [calendarControllers, setCalendarControllers] = useQueryState({
    paramName: "calendarControllers",
    defaultValue: [] as (number | string)[],
  });

  // Schedule inspection dialog state.
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDialogDate, setScheduleDialogDate] = useState<Date | null>(
    null
  );
  const [controllers, setControllers] = useState<unknown[]>([]);
  const [loadingControllers, setLoadingControllers] = useState(false);
  const calendarRefreshRef = useRef<(() => void) | null>(null);

  // Calendar plumbing — backed by RTK Query lazy triggers + mutations.
  const fetchCalendarEvents = useCallback(
    (params: Record<string, unknown>) =>
      triggerCalendarEvents(params).unwrap(),
    [triggerCalendarEvents]
  );

  const fetchControllers = useCallback(() => {
    setLoadingControllers(true);
    return triggerCalendarControllers(undefined)
      .unwrap()
      .then((result: unknown) => {
        const r = result as { controllers?: unknown[] };
        if (r?.controllers) setControllers(r.controllers);
        setLoadingControllers(false);
        return result;
      })
      .catch(() => {
        setLoadingControllers(false);
      });
  }, [triggerCalendarControllers]);

  const handleUpdateControllerColor = useCallback(
    (userId: number, color: string) =>
      updateControllerColor({ userId, color }).unwrap(),
    [updateControllerColor]
  );

  const handleCancelEvent = useCallback(
    (type: string, controlId: number) =>
      cancelCalendarEvent({ type, controlId }).unwrap(),
    [cancelCalendarEvent]
  );

  const handleUpdateEventDate = useCallback(
    (_type: string, controlId: number, newDate: string) =>
      updateEventDate({ controlId, newDate }).unwrap(),
    [updateEventDate]
  );

  const handleScheduleInspection = useCallback(
    (data: unknown) => scheduleInspection(data).unwrap(),
    [scheduleInspection]
  );

  const handleCreateControl = useCallback((date: Date) => {
    setScheduleDialogDate(date);
    setScheduleDialogOpen(true);
  }, []);

  const handleScheduleDialogClose = useCallback(() => {
    setScheduleDialogOpen(false);
    setScheduleDialogDate(null);
  }, []);

  const handleInspectionScheduled = useCallback(() => {
    if (calendarRefreshRef.current) {
      calendarRefreshRef.current();
    }
  }, []);

  const columns = useMemo(() => generateColumns({ t }), [t]);

  function handleRowClick(row: ControlRow) {
    const control = row as ControlRow & { id?: number | string };
    if (control.id != null) {
      navigate(`/dashboard/inspections/${control.id}`);
    }
  }

  return (
    <PageContainer title={t("dashboard.tab-title")}>
      <PageHeader title={t("dashboard.page-title")} />

      {/* Cards Section */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[120px] rounded-lg" />
            ))
          : cards.data.map((item, index) => (
              <DashboardInfoCard
                key={item.label}
                title={item.label}
                value={`${item.count}${item.suffix ?? ""}`}
                infoMessage={t(`dashboard.cards.info.${index}`)}
              />
            ))}
      </div>

      {/* Insights summary — directly under the KPI tiles and above the charts.
          This is a warning surface: below the calendar it sat under the fold,
          where nobody would see a red insight until they went looking. Issue
          #685 asks for it to be one of the first things a user sees.

          Renders null when the feature is off or the user lacks the permission,
          so the page closes up rather than leaving a gap. */}
      <div className="mt-8">
        <InsightsSummaryCard />
      </div>

      {/* Charts Section — still MUI: shared with CATP.
          items-start so each chart's card hugs its own content height
          rather than stretching to match the tallest sibling. */}
      <div className="mt-8 grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <Card className="relative lg:col-span-2">
          <div className="absolute top-4 right-4 z-10">
            <InfoPopover
              message={t("dashboard.charts.info.controls-over-time")}
            />
          </div>
          <CardHeader>
            <CardTitle>{t("dashboard.charts.controls-over-time")}</CardTitle>
          </CardHeader>
          <CardContent>
            <LineChart
              title=""
              noDataMessage={t("dashboard.charts.no-data")}
              data={charts.controlsOverTime}
              isLoading={charts.loading}
              xLabel={t("dashboard.charts.date")}
              yLabel={t("dashboard.charts.count")}
              tooltipLabel="{x}: {y}"
              activeFilters={[]}
              style={{
                title: { fontSize: 18, fontWeight: 600 },
                chart: {
                  padding: { top: 20, bottom: 60, left: 60, right: 50 },
                },
                line: {
                  data: {
                    stroke: BF_PALETTE.primary,
                    strokeWidth: 1.5,
                  },
                },
                tooltip: { fontSize: 9 },
                xLabel: {
                  axisLabel: { fontSize: 10, padding: 35 },
                  tickLabels: { fontSize: 9 },
                },
                yLabel: {
                  axisLabel: { fontSize: 10, padding: 40 },
                  tickLabels: { fontSize: 9 },
                },
                noDataMessage: { fontSize: 12 },
              }}
            />
          </CardContent>
        </Card>

        <Card className="relative">
          <div className="absolute top-4 right-4 z-10">
            <InfoPopover
              message={t("dashboard.charts.info.compliance-distribution")}
            />
          </div>
          <CardHeader>
            <CardTitle>
              {t("dashboard.charts.compliance-distribution")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PieChart
              title=""
              noDataMessage={t("dashboard.charts.no-data")}
              data={charts.complianceDistribution}
              isLoading={charts.loading}
              colorScale={[
                BF_PALETTE.error,
                BF_PALETTE.success,
                BF_PALETTE.info,
              ]}
              tooltipLabel="{x}: {y} ({percentage}%)"
              enableTooltip
              activeFilters={[]}
              style={{
                title: { fontSize: 18, fontWeight: 600 },
                pie: { labels: { fontSize: 14 } },
                tooltip: { fontSize: 14 },
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Control Calendar — still MUI: shared with CATP. */}
      <div className="mt-8">
        <InspectionsCalendar
          title={t("dashboard.inspections-calendar.title")}
          fetchEventsFunction={fetchCalendarEvents}
          fetchControllersFunction={fetchControllers}
          updateControllerColorFunction={handleUpdateControllerColor}
          cancelEventFunction={handleCancelEvent}
          updateEventDateFunction={handleUpdateEventDate}
          height="700px"
          itemLabelSingular={t(
            "dashboard.inspections-calendar.resource-singular"
          )}
          itemLabelPlural={t("dashboard.inspections-calendar.resource-plural")}
          showControllerFilter={canSeeAllEvents}
          canRescheduleEvents={canRescheduleEvents}
          canCancelOverdueEvents={canCancelOverdueEvents}
          canCreateControl={canCreateControl}
          defaultView={calendarView}
          onViewChange={setCalendarView}
          defaultDate={calendarDate}
          onDateChange={setCalendarDate}
          defaultSelectedControllers={calendarControllers}
          onSelectedControllersChange={setCalendarControllers}
          onCreateControl={handleCreateControl}
          onRefreshRef={(refreshFn: () => void) => {
            calendarRefreshRef.current = refreshFn;
          }}
          getEventTitle={(event: { resource?: { name?: string } }) =>
            `| ${event.resource?.name || t("calendar.unassigned")}`
          }
          onControlClick={(event: CalendarEvent) => {
            // Open control detail page in new tab to preserve calendar flow.
            if (event.controlId) {
              const path =
                event.type === "membercontrol"
                  ? `/dashboard/members/control/${event.controlId}`
                  : `/dashboard/inspections/${event.controlId}`;
              window.open(path, "_blank");
            }
          }}
          renderControlDetails={(
            control: unknown,
            props: {
              onClose: () => void;
              onViewFullDetails: () => void;
              onCancel: () => void;
              controllerColors: unknown;
              inline: boolean;
              isOverdue: boolean;
            }
          ) => (
            <ControlDetailsPreview
              control={control}
              onClose={props.onClose}
              onViewFullDetails={props.onViewFullDetails}
              onCancel={props.onCancel}
              controllerColors={props.controllerColors}
              inline={props.inline}
              isOverdue={props.isOverdue}
            />
          )}
        />
      </div>

      {/* Recent Controls Table */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{t("dashboard.recent-controls.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={recentControls.data}
            loading={recentControls.loading}
            emptyMessage={t("dashboard.recent-controls.no-data")}
            pagination={false}
            onRowClick={handleRowClick}
          />
        </CardContent>
      </Card>

      <ScheduleInspectionDialog
        open={scheduleDialogOpen}
        onClose={handleScheduleDialogClose}
        onScheduled={handleInspectionScheduled}
        defaultDate={scheduleDialogDate}
        controllers={controllers as never[]}
        loadingControllers={loadingControllers}
        scheduleInspectionFunction={handleScheduleInspection}
      />
    </PageContainer>
  );
}

